import { html, View } from 'rune-ts';
import { ProductCard, ProductLikeToggleEvent } from '../ProductCard/ProductCard';
import { classes } from '../../../../shared/util';
import klass from './ProductCardList.module.scss';
import {
  ProductCardData,
  ProductCardListData,
  ProductCardOption,
} from '../../../../features/ProductList/type';
import { DefaultProductListEmptyScreen } from './ProductCardListEmptyScreen';
import { head, map, pipe, take, toArray, uniqBy } from '@fxts/core';
import { Deque } from '../../../../shared/util/dataStructure/Deque';
import { isProductCardViews } from './utils/isProductCardViews';
import { NewSwiperView } from '../Swiper/NewSwiperView';
import { ProductCardListPluginHelper, ProductCardListPluginProps } from './helpers/plugin-helper.ts';
import { ProductCardListItem } from './views/ProductCardListItem.ts';

type GridOptions = {
  /**
   * @default true
   */
  enabled?: boolean;

  /**
   * columns 와 배타적으로 사용
   */
  rows?: number;

  /**
   * rows 와 배타적으로 사용
   */
  columns?: number;

  /**
   * @default 0
   */
  column_gap?: number;

  /**
   * @default 0
   */
  row_gap?: number;
};

type ProductCardListViewData = {
  cardViews: ProductCard[];
};

export type ProductCardListOption = ProductCardOption & {
  container_klass?: string;

  /**
   * grid 배치 옵션
   * - 이 옵션을 주입하거나 `container_klass`로 스타일을 직접 정의하세요.
   */
  grid?: GridOptions;

  limit?: number;

  plugins?: ProductCardListPluginProps;
};

export class ProductCardList extends View<ProductCardListData | ProductCardListViewData> {
  // 상품 목록에 그려진 card views
  private cardViewsDeque: Deque<ProductCardListItem>;

  // prepend 대기 중인 card views
  private readonly prependWaitingDeque = new Deque<ProductCardListItem>();

  // delete 대기 중인 card views
  private readonly deleteWaitingDeque = new Deque<ProductCardListItem>();

  private readonly option: ProductCardListOption;

  // 상품 목록이 비어있을 때 보여줄 view
  private emptyListView?: View;

  private readonly productListSwiperView?: View;

  private readonly pluginHelper: ProductCardListPluginHelper;

  // card views로 init
  constructor(cardViews: ProductCard[], option: ProductCardListOption);

  // 상품 data로 init
  constructor(data: ProductCardListData, option: ProductCardListOption);

  constructor(data: ProductCardListData | ProductCard[], option: ProductCardListOption) {
    const swiperPlugin = ProductCardListPluginHelper.getSwiperPlugin(option.plugins);

    const cardViews = ProductCardList.generateProductCardViews(
      isProductCardViews(data) ? data : data.products,
      {
        ...option,
        card_klass: swiperPlugin ? klass.swiper_product_card : '',
      },
    );

    super(isProductCardViews(data) ? { cardViews: data } : data, option);

    this.pluginHelper = new ProductCardListPluginHelper(option.plugins);

    this.cardViewsDeque = new Deque(cardViews);
    this.option = option;

    if (swiperPlugin) {
      this.productListSwiperView = new NewSwiperView({
        swiperOptions: {
          ...swiperPlugin.swiperOptions,
          slidesPerView: 'auto',
        },
        views: cardViews,
        styles: {
          swiper: klass.swiper_container,
          wrapper: klass.swiper_wrapper,
        },
      });
    }
  }

  protected override onRender() {
    super.onRender();

    this.delegate(ProductLikeToggleEvent, ProductCard, async (_, view) => {
      await view.toggleLikeButton();
    });
  }

  public get productCardViews(): ProductCardListItem[] {
    return this.cardViewsDeque.toArray();
  }

  protected override template() {
    const is_empty = !this.productCardViews.length;

    return html`
      <ul
        class="${klass.product_list} ${classes({
          [klass.empty_list]: is_empty,
          [this.option.container_klass!]: this.option.container_klass,
        })}"
        style="${this.getGridStyle()};${this.pluginHelper.getSwiperStyleVars()}"
      >
        ${is_empty ? this.getEmptyListView() : (this.productListSwiperView ?? this.productCardViews)}
      </ul>
    `;
  }

  private generateProductCardViews(items: ProductCardData[] | ProductCard[]): ProductCardListItem[] {
    return ProductCardList.generateProductCardViews(items, { ...this.option, is_lazy: false });
  }

  public static generateProductCardViews(
    items: ProductCardData[] | ProductCard[],
    option: ProductCardOption & Pick<ProductCardListOption, 'limit'>,
  ): ProductCardListItem[] {
    return pipe(
      items,
      map((item) => {
        if (item instanceof ProductCard) {
          return new ProductCardListItem({
            cardView: item,
          });
        } else {
          const cardView = new ProductCard(item, option);
          return new ProductCardListItem({
            cardView,
          });
        }
      }),
      uniqBy((cardListItemView) => cardListItemView.product_id),
      take(option.limit ?? Infinity),
      toArray,
    );
  }

  /**
   * 상품 목록 일괄로 갱신
   */
  public setProductsData(productCardViews: ProductCard[]): this;
  public setProductsData(products: ProductCardData[]): this;
  public setProductsData(items: ProductCardData[] | ProductCard[]): this {
    // 새로운 상품카드 생성하고 추가
    const new_items = this.generateProductCardViews(items);
    this.cardViewsDeque = new Deque(new_items);

    // wait queue 초기화
    this.prependWaitingDeque.clear();
    this.deleteWaitingDeque.clear();

    return this;
  }

  public get isEmpty(): boolean {
    return this.productCardViews.length === 0;
  }

  /**
   * 목록 비어있을 때 view setter
   */
  public setEmptyListView(emptyListView: View): this {
    this.emptyListView = emptyListView;
    return this;
  }

  /**
   * 목록 비어있을 때 view getter
   */
  public getEmptyListView(): View {
    if (!this.emptyListView) {
      this.emptyListView = new DefaultProductListEmptyScreen();
    }
    return this.emptyListView;
  }

  /**
   * 리스트의 앞 부분에 `ProductCard` view 추가하는 메서드
   */
  public prepend(cardView: ProductCard): void {
    const cardListItemView = new ProductCardListItem({ cardView });
    const isMatched = (item: ProductCardListItem) => item.product_id === cardListItemView.product_id;

    // 상품 목록에서 제거
    const deleted = this.cardViewsDeque.delete(isMatched);

    // prepend 대기중이면 제거하고 새 카드 추가
    this.prependWaitingDeque.delete(isMatched);
    this.prependWaitingDeque.unshift(cardListItemView);

    // 삭제된 상품 있으면 delete queue 에 추가
    if (deleted) {
      this.deleteWaitingDeque.unshift(deleted);
    }
  }

  /**
   * queue 데이터와 DOM의 싱크를 맞춰주는 메서드
   */
  public syncCardViews() {
    this.consumePrependWaitingDeque();
    this.truncByLimit();
    this.consumeDeleteWaitingDeque();
  }

  /**
   * `option.limit` 설정되어 있는 경우, limit을 초과하는 상품 카드를 삭제 예약
   */
  private truncByLimit() {
    if (this.option.limit) {
      while (this.cardViewsDeque.size > this.option.limit) {
        const lastCardView = this.cardViewsDeque.pop();
        if (lastCardView) {
          this.deleteWaitingDeque.unshift(lastCardView);
        }
      }
    }
  }

  /**
   * 예약된 prepend 처리
   */
  private consumePrependWaitingDeque(): void {
    const to_prepend_cards: ProductCardListItem[] = [];
    while (this.prependWaitingDeque.size > 0) {
      const lastCardView = this.prependWaitingDeque.pop();
      if (lastCardView) {
        this.cardViewsDeque.unshift(lastCardView);
        to_prepend_cards.push(lastCardView);
      }
    }

    // add to DOM
    this.prependCardViewElements(to_prepend_cards);
  }

  /**
   * 예약된 delete 처리
   */
  private consumeDeleteWaitingDeque(): void {
    while (this.deleteWaitingDeque.size > 0) {
      const lastCardView = this.deleteWaitingDeque.pop();
      if (lastCardView?.element()) {
        // remove to DOM
        lastCardView.element().remove();
      }
    }
  }

  /**
   * 1. `cardViews`를 element 로 변환
   * 2. 변환된 element 목록을 DOM에 prepend
   * 3. fade in
   */
  private prependCardViewElements(cardViews: ProductCardListItem[]): void {
    // create elements
    const card_els = pipe(
      cardViews,
      map((card_view) => card_view.render()),
      toArray,
    );

    // prepend elements
    this.element().prepend(...card_els);

    // fade in
    requestAnimationFrame(() => {
      cardViews.forEach((view) => view.fadeIn());
    });
  }

  public getLastOrderedAt(): Date | null {
    const head_product = head(this.productCardViews);
    return head_product && head_product.ordered_at ? head_product.ordered_at : null;
  }

  private getGridStyle(): string {
    if (!this.option.grid || this.option.grid.enabled === false) {
      return '';
    }

    const { grid } = this.option;
    const styles: string[] = ['display: grid'];

    // grid 배치
    if (grid.rows) {
      styles.push(`grid-template-rows: repeat(${grid.rows}, auto)`);
      styles.push('grid-auto-flow: column');
    } else {
      styles.push(`grid-template-columns: repeat(${grid.columns}, minmax(0, 1fr))`);
    }

    // grid gap
    if (grid.column_gap) {
      styles.push(`grid-column-gap: ${grid.column_gap}px`);
    }
    if (grid.row_gap) {
      styles.push(`grid-row-gap: ${grid.row_gap}px`);
    }

    return styles.join(';');
  }
}

export { ProductCardListPlugin } from './helpers/plugin-helper.ts';
